export type ConformanceStatus =
  | 'fully-conformant'
  | 'partially-conformant'
  | 'non-conformant'

export type WcagVersion = '2.1' | '2.2'
export type WcagLevel = 'AA' | 'AAA'

export interface KnownIssue {
  description: string
  impact: string
  workaround: string
}

export interface StatementData {
  // Step 1 - Organization
  orgName: string
  websiteName: string
  websiteUrl: string

  // Step 2 - Conformance
  wcagVersion: WcagVersion
  wcagLevel: WcagLevel
  conformanceStatus: ConformanceStatus
  assessmentDate: string

  // Step 3 - Known issues
  knownIssues: KnownIssue[]

  // Step 4 - Contact
  contactName: string
  contactEmail: string
  contactPhone: string
  responseTime: string

  // Step 5 - Technical
  technologies: string[]
  assessmentApproach: string[]
}

export const DEFAULT_DATA: StatementData = {
  orgName: '',
  websiteName: '',
  websiteUrl: '',
  wcagVersion: '2.2',
  wcagLevel: 'AA',
  conformanceStatus: 'partially-conformant',
  assessmentDate: new Date().toISOString().split('T')[0],
  knownIssues: [],
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  responseTime: '2 business days',
  technologies: ['HTML', 'CSS', 'JavaScript'],
  assessmentApproach: ['self-evaluation']
}

const CONFORMANCE_DESCRIPTIONS: Record<ConformanceStatus, string> = {
  'fully-conformant':
    'fully conforms with the WCAG standard with no exceptions',
  'partially-conformant':
    'partially conforms with the WCAG standard, as some parts of the content do not fully conform',
  'non-conformant':
    'does not conform with the WCAG standard'
}

const APPROACH_LABELS: Record<string, string> = {
  'self-evaluation': 'Self-evaluation',
  'external-audit': 'External audit by a third party',
  'automated-tools': 'Automated evaluation tools',
  'user-testing': 'User testing with assistive technology users'
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'Not specified'
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })
  } catch {
    return dateStr
  }
}

export function generateStatement(data: StatementData): string {
  const {
    orgName, websiteName, websiteUrl, wcagVersion, wcagLevel,
    conformanceStatus, assessmentDate, knownIssues,
    contactName, contactEmail, contactPhone, responseTime,
    technologies, assessmentApproach
  } = data

  const org = orgName || '[Organization Name]'
  const site = websiteName || orgName || '[Website Name]'
  const url = websiteUrl || '[Website URL]'
  const dateFormatted = formatDate(assessmentDate)

  const techList = technologies.length > 0 ? technologies.join(', ') : 'HTML, CSS, JavaScript'
  const approachList = assessmentApproach
    .map(a => APPROACH_LABELS[a] || a)
    .join(', ')

  let issuesSection = ''
  if (conformanceStatus !== 'fully-conformant' && knownIssues.length > 0) {
    const issueItems = knownIssues.map((issue, i) =>
      `  ${i + 1}. ${issue.description || 'Issue not specified'}${issue.impact ? `\n     Impact: ${issue.impact}` : ''}${issue.workaround ? `\n     Workaround: ${issue.workaround}` : ''}`
    ).join('\n\n')
    issuesSection = `\nKnown Limitations\n-----------------\nDespite our best efforts to ensure accessibility, there are some known limitations:\n\n${issueItems}\n`
  }

  const contactSection = [
    contactName && `${contactName}`,
    contactEmail && `Email: ${contactEmail}`,
    contactPhone && `Phone: ${contactPhone}`,
    `We aim to respond to accessibility feedback within ${responseTime || '2 business days'}.`
  ].filter(Boolean).join('\n')

  return `Accessibility Statement for ${site}
${'='.repeat(40 + site.length)}

This accessibility statement applies to: ${url}

Conformance Status
------------------
${org} is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards.

The Web Content Accessibility Guidelines (WCAG) define requirements for designers and developers to improve accessibility for people with disabilities. ${site} ${CONFORMANCE_DESCRIPTIONS[conformanceStatus]} with WCAG ${wcagVersion} Level ${wcagLevel}.
${issuesSection}
Feedback and Contact
--------------------
We welcome your feedback on the accessibility of ${site}. If you experience barriers or have suggestions for improvement, please contact us:

${contactSection}

If you are not satisfied with our response to your accessibility feedback, you may contact the relevant supervisory body in your jurisdiction.

Technical Specifications
------------------------
Accessibility of ${site} relies on the following technologies:
${techList.split(', ').map(t => `- ${t}`).join('\n')}

These technologies are relied upon for conformance with the accessibility standards applied.

Assessment Approach
-------------------
${org} assessed the accessibility of ${site} using the following approach(es):
${approachList.split(', ').map(a => `- ${a}`).join('\n')}

Formal Complaints
-----------------
If you are not satisfied with our response, you may contact the relevant national enforcement body. For EU member states, this is typically the national disability rights authority.

Date
----
This statement was last reviewed on ${dateFormatted}.

---
Generated with the free Accessibility Statement Generator at https://achecker.ca/accessibility-statement-generator`
}

export function generateHtmlStatement(data: StatementData): string {
  const {
    orgName, websiteName, websiteUrl, wcagVersion, wcagLevel,
    conformanceStatus, assessmentDate, knownIssues,
    contactName, contactEmail, contactPhone, responseTime,
    technologies, assessmentApproach
  } = data

  const org = orgName || '[Organization Name]'
  const site = websiteName || orgName || '[Website Name]'
  const url = websiteUrl || '[Website URL]'
  const dateFormatted = formatDate(assessmentDate)
  const techList = technologies.length > 0 ? technologies : ['HTML', 'CSS', 'JavaScript']
  const approachList = assessmentApproach.map(a => APPROACH_LABELS[a] || a)

  const issuesHtml = (conformanceStatus !== 'fully-conformant' && knownIssues.length > 0)
    ? `
    <h2>Known Limitations</h2>
    <p>Despite our best efforts to ensure accessibility, there are some known limitations:</p>
    <ol>
      ${knownIssues.map(issue => `
      <li>
        <strong>${issue.description || 'Issue'}</strong>
        ${issue.impact ? `<br>Impact: ${issue.impact}` : ''}
        ${issue.workaround ? `<br>Workaround: ${issue.workaround}` : ''}
      </li>`).join('')}
    </ol>`
    : ''

  const contactHtml = [
    contactName && `<p>${contactName}</p>`,
    contactEmail && `<p>Email: <a href="mailto:${contactEmail}">${contactEmail}</a></p>`,
    contactPhone && `<p>Phone: ${contactPhone}</p>`,
    `<p>We aim to respond within ${responseTime || '2 business days'}.</p>`
  ].filter(Boolean).join('\n      ')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Accessibility Statement — ${site}</title>
</head>
<body>
  <main>
    <h1>Accessibility Statement for ${site}</h1>
    <p>This accessibility statement applies to: <a href="${url}">${url}</a></p>

    <h2>Conformance Status</h2>
    <p>${org} is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards.</p>
    <p>${site} ${CONFORMANCE_DESCRIPTIONS[conformanceStatus]} with <a href="https://www.w3.org/TR/WCAG${wcagVersion.replace('.', '')}/">WCAG ${wcagVersion}</a> Level ${wcagLevel}.</p>
    ${issuesHtml}

    <h2>Feedback and Contact</h2>
    <p>We welcome your feedback on the accessibility of ${site}. Please contact us if you experience barriers:</p>
    ${contactHtml}

    <h2>Technical Specifications</h2>
    <p>Accessibility of ${site} relies on the following technologies:</p>
    <ul>
      ${techList.map(t => `<li>${t}</li>`).join('\n      ')}
    </ul>

    <h2>Assessment Approach</h2>
    <p>${org} assessed the accessibility of ${site} using:</p>
    <ul>
      ${approachList.map(a => `<li>${a}</li>`).join('\n      ')}
    </ul>

    <h2>Formal Complaints</h2>
    <p>If you are not satisfied with our response, you may contact the relevant national enforcement body.</p>

    <p><strong>This statement was last reviewed on ${dateFormatted}.</strong></p>

    <hr>
    <p><small>Generated with the free <a href="https://achecker.ca/accessibility-statement-generator">Accessibility Statement Generator</a> at <a href="https://achecker.ca">achecker.ca</a>.</small></p>
  </main>
</body>
</html>`
}
